import React, { useState, useContext, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { searchAI } from "../../api/AI/aiSearchApi";
import { useTheme } from "@mui/material/styles";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Skeleton,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  IconButton,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/Search";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";

const DEFAULT_PAGE_SIZE = 40;
const LIMIT_OPTIONS = [20, 40, 80, 120];

const SkeletonCard = () => (
  <Card>
    <Skeleton variant="rectangular" height={250} animation="wave" />
    <CardContent>
      <Skeleton variant="text" width="100%" height={24} />
      <Skeleton variant="text" width="60%" height={20} />
    </CardContent>
  </Card>
);

const AISearch = () => {
  const { contentMargin } = useContext(SidebarContext);
  const { t } = useTranslation();

  const theme = useTheme();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const [marcaFilter, setMarcaFilter] = useState("");
  const [temporadaFilter, setTemporadaFilter] = useState("");

  const [dragging, setDragging] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isMobileView, setIsMobileView] = useState(false);
  const [mobileCompact, setMobileCompact] = useState(false);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(true);

  const observer = useRef();
  const mobileCompactRef = useRef(false);

  const hasFilters = marcaFilter || temporadaFilter;

  const handleSearch = async () => {
    if (!query.trim() && !hasFilters) return;

    try {
      setLoading(true);
      setResults([]);
      setHasMore(true);

      const data = await searchAI(
        query.trim(),
        pageSize,
        0,
        marcaFilter,
        temporadaFilter,
      );
      const hits = data.hits || [];

      setResults(hits);
      setHasMore(hits.length >= pageSize);
    } catch (error) {
      console.error(t("aiSearch.errors.search"), error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(async () => {
    if ((!query.trim() && !hasFilters) || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);

      const data = await searchAI(
        query.trim(),
        pageSize,
        results.length,
        marcaFilter,
        temporadaFilter,
      );
      const hits = data.hits || [];

      setResults((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const filtered = hits.filter((h) => !existingIds.has(h.id));

        return [...prev, ...filtered];
      });

      setHasMore(hits.length >= pageSize);
    } catch (error) {
      console.error(t("aiSearch.errors.loadMore"), error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [query, loadingMore, hasMore, results, pageSize]);

  const lastElementRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore, loadMore],
  );

  const filteredResults = results.filter((item) => {
    if (marcaFilter && item.marca !== marcaFilter) return false;
    if (temporadaFilter && item.temporada !== temporadaFilter) return false;

    return true;
  });

  const marcas = [
    ...new Set(results.map((r) => r.marca).filter(Boolean)),
  ].sort();
  const temporadas = [
    ...new Set(results.map((r) => r.temporada).filter(Boolean)),
  ].sort();

  const processImageFile = (file) => {
    setImageLoading(true);

    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();
      img.src = reader.result;

      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const MAX_WIDTH = 512;
        const scale = MAX_WIDTH / img.width;

        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scale;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const base64 = canvas.toDataURL("image/jpeg", 0.8);

        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/ai/search-image`,
            { image: base64 },
          );

          setResults(response.data.hits || []);
        } catch (err) {
          console.error(t("aiSearch.errors.imageSearch"), err);
        } finally {
          setImageLoading(false);
        }
      };
    };

    reader.readAsDataURL(file);
  };

  const handleImageSearch = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    processImageFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    processImageFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setMarcaFilter("");
    setTemporadaFilter("");
    setHasMore(false);
  };

  const handleCardClick = (item) => {
    setSelectedImage(item);
    setDialogOpen(true);
  };

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 900;
      setIsMobileView(mobile);
      if (!mobile) {
        mobileCompactRef.current = false;
        setMobileCompact(false);
        setMobilePanelOpen(true);
      } else {
        const shouldCompact = window.scrollY > 170;
        mobileCompactRef.current = shouldCompact;
        setMobileCompact(shouldCompact);
        setMobilePanelOpen(!shouldCompact);
      }
    };
    const onScroll = () => {
      if (window.innerWidth >= 900) return;
      const y = window.scrollY;

      // Histeresis: evita parpadeo cerca del umbral.
      if (!mobileCompactRef.current && y > 170) {
        mobileCompactRef.current = true;
        setMobileCompact(true);
        setMobilePanelOpen(false);
      } else if (mobileCompactRef.current && y < 90) {
        mobileCompactRef.current = false;
        setMobileCompact(false);
        setMobilePanelOpen(true);
      }
    };
    onResize();
    onScroll();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // const searchSimilar = async () => {
  //   setDialogOpen(false);
  //   setImageLoading(true);

  //   try {
  //     const response = await axios.post(
  //       `${process.env.REACT_APP_API_URL}/api/ai/search-image`,
  //       {
  //         image: `${process.env.REACT_APP_API_URL}${selectedImage.imageUrl}`,
  //       },
  //     );

  //     setResults(response.data.hits || []);
  //   } catch (err) {
  //     console.error(err);
  //   } finally {
  //     setImageLoading(false);
  //   }
  // };

  const searchSimilar = async () => {
    setDialogOpen(false);
    setImageLoading(true);

    try {
      const imageUrl = `${process.env.REACT_APP_API_URL}${selectedImage.imageUrl}`;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = imageUrl;

      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const MAX_WIDTH = 512;
        const scale = MAX_WIDTH / img.width;

        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scale;

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const base64 = canvas.toDataURL("image/jpeg", 0.8);

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/ai/search-image`,
          { image: base64 },
        );

        setResults(response.data.hits || []);
        setImageLoading(false);
      };
    } catch (err) {
      console.error(err);
      setImageLoading(false);
    }
  };

  return (
    <Box
      sx={{
        ...contentMargin,
        mt: { xs: 1, md: 2 },
        mr: { xs: 0, md: 4 },
        px: { xs: 1.25, sm: 2 },
        pb: { xs: 10, sm: 3 },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          position: "sticky",
          top: 10,
          display: "flex",
          alignItems: "center",
          flexDirection: {
            xs: "column",
            sm: "column",
            md: "column",
            lg: "row",
          },
          gap: 2,
          mb: 4,
          zIndex: 10,
          border: dragging ? "2px dashed #1976d2" : "none",
          /* background: dragging ? "#f0f7ff" : "transparent", */
          backgroundColor: dragging
            ? theme.palette.background.paper
            : theme.palette.background.default,
          padding: dragging ? "10px" : "0px",
          borderRadius: dragging ? 2 : 0,
        }}
      >
        {isMobileView && mobileCompact && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: theme.palette.background.paper,
              px: 1.5,
              py: 0.75,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MenuIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {t("aiSearch.controls.mobilePanel")}
              </Typography>
              <Chip
                size="small"
                label={`${results.length} ${t("aiSearch.controls.resultsLabel")}`}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => setMobilePanelOpen((v) => !v)}
              aria-label={t("aiSearch.controls.toggleFilters")}
            >
              {mobilePanelOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        )}
        <Collapse
          in={!isMobileView || !mobileCompact || mobilePanelOpen}
          timeout="auto"
          unmountOnExit={isMobileView}
          sx={{ width: "100%" }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              flexDirection: { xs: "column", sm: "column", md: "row", lg: "row" },
            }}
          >
          <TextField
            fullWidth
            label={t("aiSearch.controls.searchLabel")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{
              backgroundColor: theme.palette.background.paper,
              minWidth: 180,
              flex: 1,
            }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 2,

              flexDirection: {
                xs: "column",
                sm: "column",
                md: "row",
                lg: "row",
              },
            }}
          >
            <FormControl sx={{ minWidth: 160, flex: 1 }}>
              <InputLabel>{t("aiSearch.controls.brandLabel")}</InputLabel>
              <Select
                value={marcaFilter}
                label={t("aiSearch.controls.brandLabel")}
                onChange={(e) => setMarcaFilter(e.target.value)}
                sx={{ backgroundColor: theme.palette.background.paper }}
              >
                <MenuItem value="">{t("aiSearch.controls.allBrands")}</MenuItem>
                {marcas.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 140, flex: 1 }}>
              <InputLabel>{t("aiSearch.controls.seasonLabel")}</InputLabel>
              <Select
                value={temporadaFilter}
                label={t("aiSearch.controls.seasonLabel")}
                onChange={(e) => setTemporadaFilter(e.target.value)}
                sx={{ backgroundColor: theme.palette.background.paper }}
              >
                <MenuItem value="">{t("aiSearch.controls.allSeasons")}</MenuItem>
                {temporadas.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 80, flex: 1 }}>
              <InputLabel>{t("aiSearch.controls.resultsLabel")}</InputLabel>
              <Select
                value={pageSize}
                label={t("aiSearch.controls.resultsLabel")}
                onChange={(e) => setPageSize(Number(e.target.value))}
                sx={{ backgroundColor: theme.palette.background.paper }}
              >
                {LIMIT_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: "100%",
              flexDirection: { xs: "column", sm: "row" },
              mt: 2,
            }}
          >
          <Button
            variant="contained"
            component="label"
            sx={{
              height: "3.8em",
              width: { xs: "100%", sm: "33.3%" },
              backgroundColor: "#1976d280",
            }}
          >
            {t("aiSearch.controls.imageButton")}
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={handleImageSearch}
            />
          </Button>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            sx={{
              height: "3.8em",
              width: { xs: "100%", sm: "33.3%" },
              backgroundColor: "#00c2a380",
            }}
          >
            {t("aiSearch.controls.searchButton")}
          </Button>
          <Button
            variant="contained"
            onClick={handleClear}
            sx={{
              height: "3.8em",
              width: { xs: "100%", sm: "33.3%" },
              backgroundColor: "#ffca1c8a",
            }}
          >
            {t("aiSearch.controls.clearButton")}
          </Button>{" "}
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ mt: 1, mb: 2 }}>
        <Box
          sx={{
            mb: 2,
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.14) 0%, rgba(0,194,163,0.12) 100%)",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="overline"
                sx={{ opacity: 0.9, letterSpacing: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <AutoAwesomeIcon fontSize="small" />
                {t("aiSearch.hero.overline")}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1, mb: 1 }}>
                {t("aiSearch.hero.title")}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t("aiSearch.hero.description")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: 0 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t("aiSearch.hero.whatPageDoes")}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.75 }}>
                    {t("aiSearch.hero.feature1")}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.75 }}>
                    {t("aiSearch.hero.feature2")}
                  </Typography>
                  <Typography variant="body2">
                    {t("aiSearch.hero.feature3")}
                  </Typography>
                  <Box
                    sx={{
                      mt: 1.5,
                      p: 1,
                      borderRadius: 1.5,
                      backgroundColor: "#050505",
                      border: "1px solid #303030",
                    }}
                  >
                    <Typography sx={{ color: "#fff", fontSize: "0.66rem", mb: 0.75 }}>
                      {t("aiSearch.hero.mockTitle")}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5, mb: 0.75 }}>
                      <Box sx={{ px: 0.8, py: 0.25, border: "1px solid #707070", color: "#d4d4d4", fontSize: "0.6rem" }}>
                        {t("aiSearch.hero.mockFilterAll")}
                      </Box>
                      <Box sx={{ px: 0.8, py: 0.25, border: "1px solid #707070", color: "#d4d4d4", fontSize: "0.6rem" }}>
                        {t("aiSearch.hero.mockFilterDesigner")}
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", gap: 0.75 }}>
                      <Box sx={{ flex: 1, minHeight: 56, border: "1px solid #707070", display: "grid", placeItems: "center", color: "#9e9e9e", fontSize: "1rem" }}>
                        +
                      </Box>
                      <Box sx={{ flex: 1.4, minHeight: 56, border: "1px solid #707070", position: "relative", background: "linear-gradient(135deg,#efefef,#dcdcdc)" }}>
                        <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#111", fontSize: "0.58rem", fontWeight: 700 }}>
                          {t("aiSearch.hero.mockCard")}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", height: "100%" }}>
              <CardContent>
                <SearchIcon sx={{ mb: 1, color: "primary.main" }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("aiSearch.cards.textTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("aiSearch.cards.textDesc")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", height: "100%" }}>
              <CardContent>
                <ImageSearchIcon sx={{ mb: 1, color: "success.main" }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("aiSearch.cards.imageTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("aiSearch.cards.imageDesc")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", height: "100%" }}>
              <CardContent>
                <FilterAltIcon sx={{ mb: 1, color: "warning.main" }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {t("aiSearch.cards.filterTitle")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("aiSearch.cards.filterDesc")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {imageLoading && (
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>
            {t("aiSearch.loading.searchingSimilar")}
          </Typography>
        </Box>
      )}

      {!loading && !imageLoading && results.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {filteredResults.map((item, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={item.id}
              ref={index === results.length - 1 ? lastElementRef : null}
            >
              <Card
                sx={{ position: "relative", cursor: "pointer" }}
                onClick={() => handleCardClick(item)}
              >
                <CardMedia
                  component="img"
                  height="250"
                  image={`${process.env.REACT_APP_API_URL}${item.imageUrl}`}
                  alt={item.filename}
                />

                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    display: "flex",
                    gap: 1,
                  }}
                >
                  {item.marca && (
                    <Chip
                      label={item.marca}
                      size="small"
                      sx={{ background: "black", color: "white" }}
                    />
                  )}

                  {item.temporada && (
                    <Chip
                      label={item.temporada}
                      size="small"
                      sx={{ background: "#1976d2", color: "white" }}
                    />
                  )}
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{t("aiSearch.dialog.title")}</DialogTitle>

        <DialogContent>
          {selectedImage && (
            <img
              src={`${process.env.REACT_APP_API_URL}${selectedImage.imageUrl}`}
              style={{ width: "100%", borderRadius: 6 }}
              alt=""
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {t("aiSearch.dialog.cancel")}
          </Button>

          <Button variant="contained" onClick={searchSimilar}>
            {t("aiSearch.dialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AISearch;
